import { useTranslation } from 'react-i18next'
import { useMediaQuery } from 'usehooks-ts'


import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { CartPreview } from '@features/cart'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesBranch } from '@widgets/categories-branch'
import ProductsInfiniteScroll from '@widgets/products-infinite-scroll'


import { IMAGE_SIZES, URL_PAGE_CATALOG } from '@shared/contants'
import { PreviouslyOrderedProductsQuery, PreviouslyOrderedProductsQueryVariables, Product, usePreviouslyOrderedProductsQuery } from '@shared/generated'
import useFetchMorePagination from '@shared/hooks/useFetchMorePagination'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

const LIMIT = 12

export default function PreviouslyOrderedProductsPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const isTablet = useMediaQuery('(max-width: 991px)')
  const { shopId } = useAddressStore()

  const { data: previouslyOrderedProductsData, loading: previouslyOrderedProductsLoading, fetchMore } = usePreviouslyOrderedProductsQuery({
    variables: {
      shopId: shopId,
      style: IMAGE_SIZES.LARGE,
      pager: {
        limit: LIMIT
      }
    }
  })
  
  const { hasMore, loadingMore, fetchNextPage } =
    useFetchMorePagination<
      PreviouslyOrderedProductsQuery,
      PreviouslyOrderedProductsQueryVariables
    >({
      loading: previouslyOrderedProductsLoading,
      fetchMore,
      pageVariable: 'pager',
      usePager: true,
      variables: {
        shopId,
        pager: { limit: LIMIT }
      },
      updaterFunction (cached, fetched) {
        const prev = cached?.previouslyOrderedProducts?.edges ?? []
        const curr = fetched?.previouslyOrderedProducts?.edges ?? []

        return {
          hasMore: curr.length > 0,
          result: {
            ...cached,
            previouslyOrderedProducts: {
              ...cached.previouslyOrderedProducts,
              edges: [ ...prev, ...curr ],
            },
          } as PreviouslyOrderedProductsQuery,
        }
      },
    })

  const totalProducts = previouslyOrderedProductsData?.previouslyOrderedProducts?.pageInfo?.total ?? 0

  return (
    <Container>
      <PageGrid aside={<CartPreview />}>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: URL_PAGE_CATALOG
                },
                {
                  title: tp('previouslyOrdered')
                }
              ]}
            />
            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>
              </div>
            )}
            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                <TitleWithCount
                  className={styles.root__title}
                  title={tp('previouslyOrdered')}
                >
                  {totalProducts !== null && (
                    <>
                      {totalProducts}{' '}
                      {getNoun(
                        totalProducts as number,
                        tp('product.1'),
                        tp('product.2'),
                        tp('product.3'),
                      )}
                    </>
                  )}
                </TitleWithCount>
              </div>
              <ProductsInfiniteScroll
                products={previouslyOrderedProductsData?.previouslyOrderedProducts?.edges as Product[]}
                loading={previouslyOrderedProductsLoading}
                fetchNextPage={fetchNextPage}
                hasMore={hasMore}
                loadingMore={loadingMore}
              />
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}